import React, { useEffect, useState, useMemo, useRef } from "react";
import { useGameState } from "../providers/GameStateProvider";
import { useSupabase } from "../providers/SupabaseProvider";
import { BootScene } from "../game/scenes/BootScene";
import { PreloadScene } from "../game/scenes/PreloadScene";
import { BudgetSceneReact } from "../game/scenes/BudgetSceneReact";
import { CatShowcaseScene } from "../game/scenes/CatShowcaseScene";
import { GameStage } from "./GameStage";
import { CoordinatePlaneGrid } from "./CoordinatePlaneGrid";
import { ScoreLabel } from "./ScoreLabel";
import {
  TOOLBAR_HEIGHT,
  BOTTOM_NAVIGATION_HEIGHT,
} from "../game/BackgroundImage";
import { CategoryWithBounds } from "../config/types";
import { useHintVisibility } from "../hooks/useHintVisibility";
import { calculateBudgetPercentages } from "../utils/budgetCalculations";
import GoalPopup from "./GoalPopup";

interface GameState {
  categories: CategoryWithBounds[];
  adultMode: boolean;
  level: {
    id: string;
    nickname: string;
  } | null;
}

interface GameCanvasProps {
  levelId?: string;
}

const GameCanvas: React.FC<GameCanvasProps> = ({ levelId }) => {
  const { currentLevel, adultMode } = useGameState();
  const { user } = useSupabase();
  const { showHeartSprite } = useHintVisibility();
  const previousLevelIdRef = useRef<string | undefined>(levelId);

  const [gameState, setGameState] = useState<GameState>({
    categories:
      currentLevel?.categories?.map((cat) => ({
        name: cat.name,
        spent: cat.spent,
        budgeted: cat.budgeted,
        necessary: cat.necessary,
        savings: cat.savings,
        bounds: { x: 0, y: 0, w: 0, h: 0 },
        isBottomRow: false,
      })) || [],
    adultMode: adultMode,
    level: currentLevel
      ? { id: String(currentLevel.id), nickname: currentLevel.nickname }
      : null,
  });

  // Keep track of which cat is currently selected (donor) in Phaser to
  // allow the React overlay (AppleRing) to highlight that cat.
  const [selectedCatIndex, setSelectedCatIndex] = useState<
    number | undefined
  >();
  // React overlay (BudgetGrid, labels, etc.) stays hidden until Phaser scene
  // explicitly tells us it is ready via "phaser-overlay-toggle". This avoids
  // UI elements flashing while PreloadScene is still loading assets.
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);

  // State for goal popup
  const [showGoalPopup, setShowGoalPopup] = useState<boolean>(false);
  const [popupShownForLevel, setPopupShownForLevel] = useState<number | null>(
    null,
  );

  // Create Phaser config
  const gameConfig = useMemo<Phaser.Types.Core.GameConfig>(
    () => ({
      type: Phaser.AUTO,
      transparent: true,
      width: window.innerWidth,
      height: window.innerHeight,
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: [BootScene, PreloadScene, BudgetSceneReact, CatShowcaseScene],
      physics: {
        default: "arcade",
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false,
        },
      },
    }),
    [],
  );

  // Listen for category updates from Phaser
  useEffect(() => {
    // Helper to toggle input
    const setSceneInputEnabled = (enabled: boolean) => {
      const scene = (window as any).PhaserSceneReact;
      if (scene && scene.input) {
        scene.input.enabled = enabled;
      }
    };

    const handleCategoriesUpdated = (event: CustomEvent) => {
      const { categories } = event.detail;

      setGameState((prev) => ({
        ...prev,
        categories: categories.map((cat: any) => ({
          name: cat.name,
          spent: cat.spent,
          budgeted: cat.budgeted,
          necessary: cat.necessary,
          savings: cat.savings,
          bounds: { x: 0, y: 0, w: 0, h: 0 }, // Not used in new system
          isBottomRow: false, // Not used in new system
        })),
      }));
    };

    const handleCatClick = (event: CustomEvent) => {
      const { selectedIndex } = (event as any).detail ?? {};

      // selectedIndex is -1 when no cat is selected
      setSelectedCatIndex(selectedIndex >= 0 ? selectedIndex : undefined);
    };

    const handleSceneReady = (event: CustomEvent) => {};

    const handleLevelComplete = (event: CustomEvent) => {
      // Disable input while congrats modal is shown
      setSceneInputEnabled(false);
    };

    const handleModalClosed = () => {
      setSceneInputEnabled(true);
    };

    // Handle force scene restart for retry functionality
    const handleForceSceneRestart = (event: CustomEvent) => {
      const { categories } = event.detail;
      const game = (window as any).PhaserGame;
      if (game) {
        // Stop the current scene if running
        const runningScene = (window as any).PhaserSceneReact;
        if (runningScene && runningScene.scene.key === "BudgetSceneReact") {
          console.log("Force restarting BudgetSceneReact for retry");
          game.scene.stop("BudgetSceneReact");
        }

        // Start fresh scene with new data
        game.scene.start("BudgetSceneReact", {
          categories: categories,
        });
      }
    };

    // Window resize handler to keep Phaser registry updated with React's dimensions
    const updatePhaserDimensions = () => {
      const game = (window as any).PhaserGame;
      if (game) {
        const reactScreenWidth = window.innerWidth;
        const reactScreenHeight = window.innerHeight - TOOLBAR_HEIGHT;
        game.registry.set("reactScreenWidth", reactScreenWidth);
        game.registry.set("reactScreenHeight", reactScreenHeight);
      }
    };

    // Add event listeners
    document.addEventListener(
      "phaser-categories-updated",
      handleCategoriesUpdated as EventListener,
    );
    document.addEventListener(
      "phaser-scene-ready",
      handleSceneReady as EventListener,
    );
    window.addEventListener(
      "phaser-cat-clicked",
      handleCatClick as EventListener,
    );
    window.addEventListener(
      "levelComplete",
      handleLevelComplete as EventListener,
    );
    window.addEventListener("modalClosed", handleModalClosed as EventListener);
    window.addEventListener("resize", updatePhaserDimensions);
    window.addEventListener(
      "forceSceneRestart",
      handleForceSceneRestart as EventListener,
    );

    const handleOverlayToggle = (event: CustomEvent) => {
      const { visible } = (event as any).detail ?? { visible: true };
      setOverlayVisible(visible);
    };

    window.addEventListener(
      "phaser-overlay-toggle",
      handleOverlayToggle as EventListener,
    );

    return () => {
      document.removeEventListener(
        "phaser-categories-updated",
        handleCategoriesUpdated as EventListener,
      );
      document.removeEventListener(
        "phaser-scene-ready",
        handleSceneReady as EventListener,
      );
      window.removeEventListener(
        "phaser-cat-clicked",
        handleCatClick as EventListener,
      );
      window.removeEventListener(
        "levelComplete",
        handleLevelComplete as EventListener,
      );
      window.removeEventListener(
        "modalClosed",
        handleModalClosed as EventListener,
      );
      window.removeEventListener("resize", updatePhaserDimensions);
      window.removeEventListener(
        "forceSceneRestart",
        handleForceSceneRestart as EventListener,
      );
      window.removeEventListener(
        "phaser-overlay-toggle",
        handleOverlayToggle as EventListener,
      );
    };
  }, []);

  // Update gameState when level changes
  useEffect(() => {
    if (currentLevel) {
      setGameState((prev) => ({
        ...prev,
        categories:
          currentLevel.categories?.map((cat) => ({
            name: cat.name,
            spent: cat.spent,
            budgeted: cat.budgeted,
            necessary: cat.necessary,
            savings: cat.savings,
            bounds: { x: 0, y: 0, w: 0, h: 0 },
            isBottomRow: false,
          })) || [],
        adultMode: adultMode,
        level: {
          id: String(currentLevel.id),
          nickname: currentLevel.nickname,
        },
      }));
    }
  }, [currentLevel, adultMode]);

  // Sync game state with Phaser registry when data changes
  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).PhaserGame) {
      const game = (window as any).PhaserGame;

      // Calculate React's exact screen dimensions
      const reactScreenWidth = window.innerWidth;
      const reactScreenHeight = window.innerHeight - TOOLBAR_HEIGHT + 56; // TODO: remove 56

      // Always update registry with latest data
      if (currentLevel) {
        game.registry.set("level", currentLevel);

        // Set initial categories in registry so PreloadScene can use them
        if (currentLevel.categories) {
          game.registry.set("initialCategories", currentLevel.categories);
        }
      }
      game.registry.set("adultMode", adultMode);
      if (user) {
        game.registry.set("uid", user.id);
      }

      // Pass hint visibility settings to Phaser
      game.registry.set("hideHeartSprite", !showHeartSprite);

      // Pass React's exact screen dimensions to Phaser
      game.registry.set("reactScreenWidth", reactScreenWidth);
      game.registry.set("reactScreenHeight", reactScreenHeight);

      // Only restart the scene if the level ID actually changed (not just on other prop changes)
      const currentLevelId =
        levelId || (currentLevel ? String(currentLevel.id) : undefined);
      const hasLevelChanged =
        currentLevelId && currentLevelId !== previousLevelIdRef.current;

      if (hasLevelChanged && currentLevel) {
        console.log(
          `Level changed from ${previousLevelIdRef.current} to ${currentLevelId} - restarting BudgetSceneReact`,
        );

        // Stop the current scene if running
        const runningScene = (window as any).PhaserSceneReact;
        if (runningScene && runningScene.scene.key === "BudgetSceneReact") {
          console.log(
            "Stopping previous BudgetSceneReact before starting new level",
          );
          game.scene.stop("BudgetSceneReact");
        }

        // Start fresh scene with new level data
        console.log(
          "Starting fresh BudgetSceneReact for level",
          currentLevelId,
        );
        game.scene.start("BudgetSceneReact", {
          categories: currentLevel.categories,
        });

        // Update the reference
        previousLevelIdRef.current = currentLevelId;
      }
    }
  }, [currentLevel, adultMode, user, levelId, showHeartSprite]);

  // pause / resume input based on modal
  useEffect(() => {
    const pause = () => {
      const scene = (window as any).PhaserSceneReact;
      if (scene) scene.input.enabled = false;
    };
    const resume = () => {
      const scene = (window as any).PhaserSceneReact;
      if (scene) scene.input.enabled = true;
    };

    window.addEventListener("levelComplete", pause as EventListener);
    window.addEventListener("modalClosed", resume as EventListener); // dispatch this from CongratsModal on onClose()

    return () => {
      window.removeEventListener("levelComplete", pause as EventListener);
      window.removeEventListener("modalClosed", resume as EventListener);
    };
  }, []);

  // Refresh heart visibility when hint settings change
  useEffect(() => {
    const scene = (window as any).PhaserSceneReact;
    if (scene && scene.refreshHeartVisibility) {
      console.log("Refreshing heart visibility due to hint setting change");
      scene.refreshHeartVisibility();
    }
  }, [showHeartSprite]);

  const [catPositions, setCatPositions] = useState<{ x: number; y: number }[]>(
    [],
  );
  const [uiMargin, setUiMargin] = useState(0);
  const [removedCats, setRemovedCats] = useState<Set<number>>(new Set());

  // Handle cat position updates from Phaser
  const handleCatPositionUpdate = (e: CustomEvent) => {
    const { index, x, y } = e.detail;
    setCatPositions((prev) => {
      const newPositions = [...prev];
      newPositions[index] = { x, y };
      return newPositions;
    });
  };

  useEffect(() => {
    window.addEventListener(
      "phaser-cat-position-updated",
      handleCatPositionUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "phaser-cat-position-updated",
        handleCatPositionUpdate as EventListener,
      );
    };
  }, []);

  // Handle cat removed events from Phaser
  useEffect(() => {
    const handleCatRemoved = (e: CustomEvent) => {
      const { index } = e.detail as { index: number };
      setRemovedCats((prev) => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
    };

    window.addEventListener(
      "phaser-cat-removed",
      handleCatRemoved as EventListener,
    );

    return () => {
      window.removeEventListener(
        "phaser-cat-removed",
        handleCatRemoved as EventListener,
      );
    };
  }, []);

  // Reset removed cats when level changes
  useEffect(() => {
    setRemovedCats(new Set());
  }, [currentLevel]);

  const [fixedBounds, setFixedBounds] = useState<
    { minSurplus: number; maxSurplus: number } | undefined
  >();

  const handleUiMargin = (e: CustomEvent) => setUiMargin(e.detail as number);
  useEffect(() => {
    window.addEventListener(
      "phaser-ui-margin",
      handleUiMargin as EventListener,
    );
    return () => {
      window.removeEventListener(
        "phaser-ui-margin",
        handleUiMargin as EventListener,
      );
    };
  }, [handleUiMargin]);

  // Get fixed bounds from Phaser scene
  useEffect(() => {
    const getFixedBounds = () => {
      const scene = (window as any).PhaserSceneReact;
      if (scene && scene.fixedBounds) {
        setFixedBounds(scene.fixedBounds);
      }
    };

    // Try to get bounds immediately
    getFixedBounds();

    // Also listen for scene updates
    const handleSceneUpdate = () => {
      setTimeout(getFixedBounds, 100); // Small delay to ensure scene is ready
    };

    window.addEventListener(
      "phaser-overlay-toggle",
      handleSceneUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "phaser-overlay-toggle",
        handleSceneUpdate as EventListener,
      );
    };
  }, []);

  // Calculate budget percentages reactively
  const budgetPercentages = useMemo(() => {
    return calculateBudgetPercentages(
      gameState.categories.map((cat) => ({
        name: cat.name,
        spent: cat.spent,
        budgeted: cat.budgeted,
        necessary: cat.necessary,
        savings: cat.savings,
      })),
    );
  }, [gameState.categories]);

  // Check if current level is yic-kane or yic-liz and show goal popup
  useEffect(() => {
    // Reset popup state when level changes
    if (!currentLevel?.nickname?.startsWith('yic-')) {
      setShowGoalPopup(false);
      setPopupShownForLevel(null);
      return;
    }

    // Show popup for yic levels only if not shown for this level ID
    if (currentLevel?.nickname?.startsWith('yic-') && 
        currentLevel?.id && 
        popupShownForLevel !== currentLevel.id) {
      // Small delay to ensure the level is fully loaded
      const timer = setTimeout(() => {
        setShowGoalPopup(true);
        setPopupShownForLevel(currentLevel.id);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [currentLevel?.nickname, currentLevel?.id, popupShownForLevel]);

  const handleGoalPopupClose = () => {
    setShowGoalPopup(false);
  };

  return (
    <div
      className="game-background"
      style={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <GameStage gameConfig={gameConfig}>
        {
          overlayVisible && (
            <>
              {/* Render coordinate plane grid with cat boxes */}
              <CoordinatePlaneGrid
                categories={gameState.categories.map((cat) => ({
                  name: cat.name,
                  spent: cat.spent,
                  budgeted: cat.budgeted,
                  necessary: cat.necessary,
                  savings: cat.savings,
                }))}
                adultMode={gameState.adultMode}
                screenWidth={window.innerWidth}
                screenHeight={window.innerHeight - TOOLBAR_HEIGHT}
                selectedCategoryIndex={selectedCatIndex}
                transferAnimation={undefined}
                catPositions={catPositions}
                topMargin={uiMargin}
                levelPrecision={currentLevel?.dial_precision ?? 100}
                fixedBounds={fixedBounds}
                hiddenCatIndices={removedCats}
                budgetPercentages={budgetPercentages}
              />
            </>
          ) /* overlayVisible */
        }
      </GameStage>

      {/* Goal Popup for yic levels */}
      {showGoalPopup && (
        <GoalPopup onClose={handleGoalPopupClose} levelNickname={currentLevel?.nickname} />
      )}
    </div>
  );
};

export default GameCanvas;
